import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole, isSuperAdmin } from "@/lib/accountRole";

// A full-width band that NAMES the current staff mode across the top of every admin page.
// Colour alone makes people memorise which is which; the word doesn't — so the band never
// ships the colour without the label.
//   ADMIN        → band #A33D28 / text #FFE8E2 · left "⚑ ADMIN MODE"       · right "OPTICSWAP ADMIN"
//   SUPER ADMIN  → band #C2600F / text #FFEBD6 · left "⚠ SUPER ADMIN MODE" · right "FULL ACCESS · EXIT"
// The band renders ONLY inside the admin portal (path /admin) while in ADMIN MODE — never on a
// member-facing page (absent, not muted, not thinner). That absence is what makes it mean
// something: an admin browsing the marketplace as a member sees the member state. Switching
// modes recolours + relabels immediately (mode is live session state, no reload).
// Grayscale note: #C2600F (super) reads LIGHTER than #A33D28 (admin), so the two stay
// distinguishable by lightness, not only hue.
export const ADMIN_MODE_BAR_HEIGHT_PX = 30;

const BAND_CLS = "font-mono font-bold uppercase";
const BAND_STYLE = { fontSize: "10.5px", letterSpacing: "0.22em", lineHeight: 1 };

export default function AdminModeBar() {
  const { user, adminMode, setAdminMode } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Impersonation (specced-not-built) — a full red banner shown wherever you are.
  if (adminMode === "impersonate") {
    return (
      <div
        role="status"
        aria-label="Viewing as user"
        data-testid="admin-impersonate-bar"
        className="fixed top-0 inset-x-0 z-[70] h-9 flex items-center justify-center gap-2 px-4 text-sm font-semibold tracking-wide bg-red-600 text-white shadow-md"
      >
        <span className="truncate">VIEWING AS USER</span>
      </div>
    );
  }

  // The band belongs to the admin CONTEXT, not just the /admin URL. /inbox is a shared route that
  // renders inside the admin shell while in admin mode, so the band must ride there too.
  const onAdmin = pathname.startsWith("/admin") || pathname.startsWith("/inbox");
  if (!isAdminRole(user?.role) || adminMode !== "admin" || !onAdmin) return null;

  const superAdmin = isSuperAdmin(user?.role);
  const bg = superAdmin ? "#C2600F" : "#A33D28";
  const fg = superAdmin ? "#FFEBD6" : "#FFE8E2";
  const leftLabel = superAdmin ? "\u26A0 SUPER ADMIN MODE" : "\u2691 ADMIN MODE";

  const exit = () => { setAdminMode("user"); navigate("/feed"); };

  return (
    <div
      role="status"
      aria-label={superAdmin ? "Super admin mode active" : "Admin mode active"}
      data-testid="admin-mode-bar"
      className="fixed top-0 inset-x-0 z-[70] flex items-center justify-between px-4"
      style={{ height: `${ADMIN_MODE_BAR_HEIGHT_PX}px`, backgroundColor: bg }}
    >
      <span className={BAND_CLS} style={{ ...BAND_STYLE, color: fg }} data-testid="admin-mode-bar-label">
        {leftLabel}
      </span>
      {superAdmin ? (
        <span className={BAND_CLS} style={{ ...BAND_STYLE, color: fg }} data-testid="admin-mode-bar-right">
          FULL ACCESS &middot;{" "}
          <button
            type="button"
            onClick={exit}
            data-testid="admin-mode-bar-exit"
            className="underline underline-offset-2 hover:opacity-80 transition-opacity"
            style={{ letterSpacing: "0.22em", color: fg }}
          >
            EXIT
          </button>
        </span>
      ) : (
        <span className={BAND_CLS} style={{ ...BAND_STYLE, color: fg }} data-testid="admin-mode-bar-right">
          OPTICSWAP ADMIN &middot;{" "}
          <button
            type="button"
            onClick={exit}
            data-testid="admin-mode-bar-exit"
            className="underline underline-offset-2 hover:opacity-80 transition-opacity"
            style={{ letterSpacing: "0.22em", color: fg }}
          >
            EXIT
          </button>
        </span>
      )}
    </div>
  );
}
